// Campaign API client
import axios from 'axios';
import client from './client';

export interface Campaign {
  id: string;
  campaign_id: string;
  platform: string;
  quarter: string;
  market: string;
  business_unit: string;
  nameplate: string;
  campaign_name: string;
  plan_id: string | null;
  // ISO timestamp of when the campaign's media plan was approved (null when
  // the campaign has no plan or the plan predates approval timestamps).
  plan_approved_at: string | null;
  channel: string | null;
  buy_type: string | null;
  tier: string | null;
  placement: string;
  format: string;
  size: string;
  video_length: string | null;
  language: string;
  // Read-only MAP field: "DCO Enabled", "No DCO", or null. DCO campaigns
  // follow a different flow and are visually distinguished in the UI.
  dco_status: string | null;
  price_assets: string;
  start_date: string | null;
  end_date: string | null;

  // User-editable fields
  preview_link: string[] | null;
  live: boolean | null;
  live_date: string | null;
  fordshare_entries: FordShareEntry[] | null;

  // Future automation fields
  expiration_date: string | null;
  deployment_deck: string | null;
  asset_type_id: string | null;

  // Note: Asset-specific fields (post_copies, headlines, descriptions, cta, url)
  // have been moved to CampaignAsset interface

  // Status (calculated automatically based on fields)
  status: string | null;

  // SLA fields (calculated dynamically)
  sla_status: 'on_track' | 'warning' | 'breached' | 'not_applicable' | null;
  sla_phase: 'planning' | 'creative_localisation' | 'media_activation' | 'completed' | null;
  sla_days_remaining: number | null;
  sla_deadline: string | null; // ISO date of the SLA deadline (backend-computed, weekday-aware)
  sla_message: string | null;

  // Audit fields
  last_edited_by: {
    id: string;
    name: string;
    email: string;
  } | null;
  last_edited_at: string | null;

  // Archive fields
  is_archived: boolean;
  archived_at: string | null;
  archived_by: {
    id: string;
    name: string;
    email: string;
  } | null;
  archive_reason: string | null;

  // Preview approval fields
  preview_approved_at: string | null;
  preview_approved_by: {
    id: string;
    name: string;
    email: string;
  } | null;
}

export interface FordShareEntry {
  id: string;
  delivery_date: string;
}

export interface UpdateCampaignRequest {
  campaign_start_date?: string | null;
  campaign_end_date?: string | null;
  preview_link?: string | string[] | null;
  live?: boolean | null;
  live_date?: string | null;
  fordshare_entries?: FordShareEntry[] | null;
  asset_type_id?: string | null;
}

export interface CampaignsResponse {
  campaigns: Campaign[];
}

export async function fetchCampaigns(includeArchived: boolean = false): Promise<CampaignsResponse> {
  const response = await client.get<CampaignsResponse>('/campaigns', {
    params: { include_archived: includeArchived },
  });
  return response.data;
}

export async function fetchCampaign(campaignId: string): Promise<Campaign | undefined> {
  const response = await client.get<CampaignsResponse>('/campaigns');
  return response.data.campaigns.find((c) => c.id === campaignId);
}

export async function updateCampaign(
  campaignId: string,
  data: UpdateCampaignRequest,
): Promise<{ success: boolean; message: string }> {
  const response = await client.patch(`/campaigns/${campaignId}`, data);
  return response.data;
}

// Archive/Unarchive API
export interface ArchiveCampaignRequest {
  reason?: string;
}

export async function archiveCampaign(
  campaignId: string,
  reason?: string,
): Promise<{ success: boolean; message: string }> {
  const response = await client.patch(`/campaigns/${campaignId}/archive`, {
    reason,
  });
  return response.data;
}

export async function unarchiveCampaign(
  campaignId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await client.patch(`/campaigns/${campaignId}/unarchive`);
  return response.data;
}

// Asset submission types
export interface CarouselItemData {
  headline: string;
  description: string;
  hasFile: boolean; // Whether this item has a file uploaded
}

export interface CollectionItemData {
  headline: string;
  description: string;
  hasFile: boolean; // Whether this item has a file uploaded
}

export interface AssetSubmissionRequest {
  post_copies?: string[];
  headlines?: string[];
  descriptions?: string[];
  cta?: string[];
  legal?: string;
  url?: string;
  delivery_date?: string;
  expiration_date?: string;
  asset_type_id?: string;
  status?: 'draft' | 'approved';
  approval_note?: string; // Note required for approval
  skip_validation?: boolean; // Skip validation for draft saves
  carousel_items?: CarouselItemData[]; // Carousel item metadata for validation
  collection_items?: CollectionItemData[]; // Collection item metadata for validation
  video_length?: string; // Video length this asset is for (e.g., '6"' or '15"')
  ratio?: string; // Asset ratio for multi-ratio campaigns (e.g., '1x1' or '9x16')
  variant_number?: number; // Variant number (1-8) for ad variants
  is_offer_led?: boolean; // Whether this variant is offer-led (per variant)
  offer_end_date?: string; // Offer end date (YYYY-MM-DD), required when offer-led
  uploaded_file_refs?: UploadedFileRef[]; // Pre-uploaded file refs (bypasses direct file upload)
  // Fully-ordered list of asset IDs (existing + new) for the variant. `null` marks
  // an index this submit has no row for, keeping later positions correct.
  asset_order?: (string | null)[];
}

export interface UploadedImage {
  asset_id: string;
  filename: string;
  storage_path: string;
  file_size: number;
  content_type: string;
}

// Pre-uploaded file reference (from individual file upload endpoint)
export interface UploadedFileRef {
  asset_id: string;
  filename: string;
  storage_path: string;
  file_size: number;
  content_type: string;
  ratio?: string;
  existing_asset_id?: string;
}

export interface FailedUpload {
  filename: string;
  error: string;
}

/**
 * One slot in a multi-asset format's display order. Children emit this array so
 * the parent can build `asset_order` (the list of real asset IDs) after new
 * files are uploaded and their IDs are known:
 *  - `existingAssetId` — a kept/reordered existing DB row.
 *  - `newFileIndex`     — the Nth file in the dense new-files array passed to
 *    uploadWithProgress; resolved to the returned UploadedFileRef.asset_id.
 *  - `{ empty: true }` reserves an index with no row behind it, serialised as null.
 *    A submit covers a single ratio, so a card that has no crop in that ratio must
 *    still occupy its slot — otherwise positions close up and the crops of
 *    different cards get paired together on reload.
 */
export type AssetOrderToken =
  | { existingAssetId: string }
  | { newFileIndex: number }
  | { empty: true };

export interface ValidationIssue {
  field: string;
  message: string;
  rule: string;
}

export interface ValidationFeedback {
  action: 'accepted' | 'dismissed';
  user_id?: string;
  user_email?: string;
  timestamp?: string;
  warning_message?: string;
}

export interface ValidationResult {
  is_valid: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  feedback?: Record<string, ValidationFeedback>;
  langfuse_trace_id?: string;
}

export interface AssetSubmissionResponse {
  success: boolean;
  campaign_id: string;
  asset_ids: string[];
  message: string;
  uploaded_images: UploadedImage[];
  failed_uploads?: FailedUpload[];
  validation?: ValidationResult;
}

export interface CampaignAsset {
  id: string;
  asset_name: string;
  asset_type: string;
  status: string;
  storage_path: string | null;
  file_size: number | null;
  content_type: string | null;
  video_length?: string | null; // Video length this asset is for
  ratio?: string | null; // Asset ratio for multi-ratio campaigns (e.g., "1x1", "9x16")
  variant_number?: number | null; // Variant number (1-8) for ad variants
  position?: number | null; // Display order within a (campaign, variant) group
  post_copies?: string[] | null;
  headlines?: string[] | null;
  descriptions?: string[] | null;
  cta?: string[] | null;
  legal?: string | null;
  url?: string | null;
  is_offer_led?: boolean | null;
  offer_end_date?: string | null;
  // Timestamps
  created_at: string | null;
  updated_at?: string | null;
  // Approval metadata
  signed_url?: string;
  approved_by_user_id?: string | null;
  approved_at?: string | null;
  approval_note?: string | null;
  validation_result?: ValidationResult | null;
  approved_by?: {
    name: string;
    email: string;
  } | null;
}

export interface CampaignAssetsResponse {
  campaign_id: string;
  total_assets: number;
  assets: CampaignAsset[];
}

export async function submitCampaignAssets(
  campaignId: string,
  data: AssetSubmissionRequest,
): Promise<AssetSubmissionResponse> {
  const formData = new FormData();

  // Add text fields as JSON strings (for arrays)
  if (data.post_copies) {
    formData.append('post_copies', JSON.stringify(data.post_copies));
  }
  if (data.headlines) {
    formData.append('headlines', JSON.stringify(data.headlines));
  }
  if (data.descriptions) {
    formData.append('descriptions', JSON.stringify(data.descriptions));
  }
  if (data.cta) {
    formData.append('cta', JSON.stringify(data.cta));
  }
  if (data.legal !== undefined) {
    // FastAPI coerces empty strings to null for Optional[str] Form fields, so we use
    // a sentinel value to explicitly clear the field when the user deletes the content.
    formData.append('legal', data.legal || '__clear__');
  }
  if (data.url !== undefined) {
    formData.append('url', data.url || '__clear__');
  }
  if (data.delivery_date) {
    formData.append('delivery_date', data.delivery_date);
  }
  if (data.expiration_date) {
    formData.append('expiration_date', data.expiration_date);
  }

  // Always use pre-uploaded file refs (two-phase upload via /upload-file endpoint)
  if (data.uploaded_file_refs && data.uploaded_file_refs.length > 0) {
    formData.append('uploaded_file_refs', JSON.stringify(data.uploaded_file_refs));
  }

  // Full display order of asset IDs (existing + newly uploaded) so the backend can
  // persist reorders of existing images even when nothing new is uploaded.
  if (data.asset_order && data.asset_order.length > 0) {
    formData.append('asset_order', JSON.stringify(data.asset_order));
  }

  if (data.asset_type_id) {
    formData.append('asset_type_id', data.asset_type_id);
  }
  if (data.status) {
    formData.append('status', data.status);
  }
  if (data.approval_note) {
    formData.append('approval_note', data.approval_note);
  }
  if (data.skip_validation !== undefined) {
    formData.append('skip_validation', data.skip_validation.toString());
  }

  // Add carousel/collection item metadata for validation
  if (data.carousel_items) {
    formData.append('carousel_items', JSON.stringify(data.carousel_items));
  }
  if (data.collection_items) {
    formData.append('collection_items', JSON.stringify(data.collection_items));
  }
  if (data.video_length) {
    formData.append('video_length', data.video_length);
  }
  if (data.ratio) {
    formData.append('ratio', data.ratio);
  }
  if (data.variant_number !== undefined) {
    formData.append('variant_number', data.variant_number.toString());
  }

  // Offer-led metadata. `is_offer_led` is always sent when defined so the backend
  // can distinguish "toggle is off" from "client didn't send offer data"; the date
  // is sent as an empty string when cleared so the backend clears it too.
  if (data.is_offer_led !== undefined) {
    formData.append('is_offer_led', data.is_offer_led.toString());
    formData.append('offer_end_date', data.offer_end_date ?? '');
  }

  const response = await client.post(`/campaigns/${campaignId}/assets/submit`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

/**
 * Upload a single file via direct-to-GCS signed URL to bypass Cloud Run's
 * 32 MiB request body limit.
 *
 * Flow:
 *  1. POST /request-upload-url  → get signed URL + asset_id + blob_name
 *  2. PUT  file to signed URL   → goes directly to storage.googleapis.com
 *  3. POST /complete-upload      → server validates & returns AssetUploadResult
 *
 * The function signature is kept identical to the previous implementation.
 */
export async function uploadSingleFile(
  campaignId: string,
  file: File,
  options?: {
    ratio?: string;
    assetTypeId?: string;
    existingAssetId?: string;
    onProgress?: (percent: number) => void;
    signal?: AbortSignal;
  },
): Promise<UploadedFileRef> {
  // Step 1 – Request a signed upload URL from the backend
  const { data: urlData } = await client.post<{
    asset_id: string;
    blob_name: string;
    upload_url: string;
    content_type: string;
    expires_in_seconds: number;
  }>(
    `/campaigns/${campaignId}/assets/request-upload-url`,
    {
      filename: file.name,
      content_type: file.type,
      ratio: options?.ratio,
      asset_type_id: options?.assetTypeId,
    },
    {
      signal: options?.signal,
    },
  );

  // Step 2 – PUT the file directly to GCS (bypasses Cloud Run entirely).
  // Use a standalone axios instance — no /api base URL, no Okta auth header.
  await axios.put(urlData.upload_url, file, {
    headers: { 'Content-Type': urlData.content_type },
    signal: options?.signal,
    // The GCS PUT is the actual file transfer → ~90 % of the progress bar.
    onUploadProgress: (progressEvent) => {
      if (options?.onProgress && progressEvent.total) {
        // Map 0-100 of the PUT to 5-95 of overall progress
        const putPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        const overallPercent = Math.min(5 + Math.round(putPercent * 0.9), 95);
        options.onProgress(overallPercent);
      }
    },
  });

  // Step 3 – Tell the backend the upload is done so it can validate the blob
  const { data: result } = await client.post<{
    asset_id: string;
    filename: string;
    storage_path: string;
    file_size: number;
    content_type: string;
  }>(
    `/campaigns/${campaignId}/assets/complete-upload`,
    {
      asset_id: urlData.asset_id,
      blob_name: urlData.blob_name,
      filename: file.name,
      content_type: urlData.content_type,
      ratio: options?.ratio,
      asset_type_id: options?.assetTypeId,
    },
    {
      signal: options?.signal,
    },
  );

  // Report 100 % once the backend has confirmed
  options?.onProgress?.(100);

  return {
    ...result,
    ratio: options?.ratio,
    existing_asset_id: options?.existingAssetId,
  };
}

export async function getCampaignAssets(
  campaignId: string,
  includeUrls: boolean = false,
): Promise<CampaignAssetsResponse> {
  const response = await client.get(`/campaigns/${campaignId}/assets`, {
    params: { include_urls: includeUrls },
  });
  return response.data;
}

export async function getAssetUrl(
  campaignId: string,
  assetId: string,
): Promise<{
  asset_id: string;
  asset_name: string;
  signed_url: string;
  expires_in_seconds: number;
}> {
  const response = await client.get(`/campaigns/${campaignId}/assets/${assetId}/url`);
  return response.data;
}

export async function deleteCampaignAssets(
  campaignId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await client.delete(`/campaigns/${campaignId}/assets`);
  return response.data;
}

export async function deleteCampaignAsset(
  campaignId: string,
  assetId: string,
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await client.delete(`/campaigns/${campaignId}/assets/${assetId}`);
    return response.data;
  } catch (err: unknown) {
    // Treat 404 as success — the asset is already gone (e.g. replaced by a prior save)
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 404) {
        return { success: true, message: 'Asset already deleted' };
      }
    }
    throw err;
  }
}

export async function deleteVariant(
  campaignId: string,
  variantNumber: number,
): Promise<{ success: boolean; message: string }> {
  const response = await client.delete(`/campaigns/${campaignId}/assets/variant/${variantNumber}`);
  return response.data;
}

// Approve all variants (bundle-level approval)
export interface ApproveAllVariantsRequest {
  approval_note?: string;
}

export interface ApproveAllVariantsResponse {
  success: boolean;
  campaign_id: string;
  assets_approved: number;
  variants_approved: number[];
  force_approved: boolean;
  message: string;
}

export async function approveAllVariants(
  campaignId: string,
  approvalNote?: string,
): Promise<ApproveAllVariantsResponse> {
  const response = await client.post(`/campaigns/${campaignId}/assets/approve-all`, {
    approval_note: approvalNote,
  });
  return response.data;
}

/**
 * Re-run validation on an already-saved variant without re-submitting form data.
 * The backend reads stored asset text fields, runs structural + AI validation,
 * writes validation_result back to the DB, and returns the result.
 */
export async function validateVariant(
  campaignId: string,
  variantNumber: number,
): Promise<{ variant_number: number; validation: ValidationResult }> {
  const response = await client.post(`/campaigns/${campaignId}/assets/validate-variant`, {
    variant_number: variantNumber,
  });
  return response.data;
}

/**
 * Submit Accept/Dismiss feedback for an AI-generated validation warning.
 * The backend persists the feedback into the asset's validation_result JSON
 * and sends a categorical score to Langfuse.
 */
export async function submitAIFeedback(
  campaignId: string,
  assetId: string,
  body: { warning_key: string; warning_message: string; action: 'accepted' | 'dismissed' },
): Promise<{ success: boolean; validation_result: ValidationResult }> {
  const response = await client.post(
    `/campaigns/${campaignId}/assets/${assetId}/ai-feedback`,
    body,
  );
  return response.data;
}

export async function getCampaignMarkets(): Promise<string[]> {
  const response = await client.get('/campaigns/markets');
  return response.data.markets;
}

// Campaign Comments
export type CommentStage = 'asset' | 'preview' | 'general';
export type CommentType = 'general' | 'rejection';

export interface CommentMention {
  user_id: string;
  name: string | null;
  email?: string;
}

export interface MentionInput {
  user_id: string;
  name: string;
}

export interface CampaignComment {
  id: string;
  stage: CommentStage;
  comment_type: CommentType;
  comment_text: string;
  author_email: string;
  author_name: string | null;
  created_at: string;
  can_delete: boolean;
  mentions: CommentMention[];
}

export interface CampaignCommentsResponse {
  campaign_id: string;
  comments: CampaignComment[];
}

export interface AddCommentResponse {
  success: boolean;
  comment: CampaignComment;
}

export async function getCampaignComments(
  campaignId: string,
  stage?: CommentStage,
): Promise<CampaignCommentsResponse> {
  const response = await client.get(`/campaigns/${campaignId}/comments`, {
    params: stage ? { stage } : undefined,
  });
  return response.data;
}

export async function addCampaignComment(
  campaignId: string,
  stage: CommentStage,
  commentText: string,
  commentType: CommentType = 'general',
  mentions: MentionInput[] = [],
): Promise<AddCommentResponse> {
  const response = await client.post(`/campaigns/${campaignId}/comments`, {
    stage,
    comment_type: commentType,
    comment_text: commentText,
    mentions,
  });
  return response.data;
}

export async function deleteCampaignComment(
  campaignId: string,
  commentId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await client.delete(`/campaigns/${campaignId}/comments/${commentId}`);
  return response.data;
}

// Campaign Events (Activity Feed)
export type CampaignEventType =
  | 'media_plan_imported'
  | 'media_plan_approved'
  | 'fordshare_id_set'
  | 'asset_uploaded'
  | 'asset_approved'
  | 'preview_link_added'
  | 'preview_approved'
  | 'campaign_live'
  | 'activation_workflow_reset'
  | 'comment_added'
  | 'archived'
  | 'unarchived'
  | 'field_updated';

export interface CampaignEvent {
  id: string;
  event_type: CampaignEventType;
  message: string;
  actor_email: string | null;
  actor_name: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  can_delete: boolean;
}

export interface CampaignEventsResponse {
  campaign_id: string;
  events: CampaignEvent[];
  total_count: number;
}

export async function deleteCampaignEvent(
  campaignId: string,
  eventId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await client.delete(`/campaigns/${campaignId}/events/${eventId}`);
  return response.data;
}

export async function getCampaignEvents(
  campaignId: string,
  limit: number = 50,
): Promise<CampaignEventsResponse> {
  const response = await client.get(`/campaigns/${campaignId}/events`, {
    params: { limit },
  });
  return response.data;
}

// Preview Approval (In-App)
export interface PreviewApprovalStatus {
  campaign_id: string;
  can_approve: boolean;
  is_approved: boolean;
  preview_link: string[] | null;
  approved_at: string | null;
}

export async function getPreviewApprovalStatus(campaignId: string): Promise<PreviewApprovalStatus> {
  const response = await client.get(`/campaigns/${campaignId}/preview-approval-status`);
  return response.data;
}

export interface ApprovePreviewResponse {
  success: boolean;
  message: string;
  approved_at: string;
}

export async function approvePreview(campaignId: string): Promise<ApprovePreviewResponse> {
  const response = await client.post(`/campaigns/${campaignId}/approve-preview`);
  return response.data;
}
